import { Location } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FilterComponent } from '@app/shared/components/filter/filter.component';
import { NotificationType, PaginationMeta } from '@app/shared/interfaces';
import { Filter } from '@app/shared/models/filter';
import { FilterService } from '@app/shared/services/filter/filter.service';
import { RuleService } from '@app/shared/services/rule/rule.service';
import { TitleService } from '@app/shared/services/title/title.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';

@Component({
  standalone: false,
  selector: 'sbnb-canned-responses',
  templateUrl: './canned-responses.component.html',
  styleUrls: ['./canned-responses.component.scss'],
})
export class CannedResponsesComponent implements OnInit {
  cannedResponses: any;
  loading: boolean;
  paginationData: PaginationMeta;
  currentOffset: number;
  searchCriteria: string;
  activeFilters: Filter[] = [];
  segmentFilterDefaults: any;

  @ViewChild('filterComponent')
  filterComponent: FilterComponent;

  constructor(
    private router: Router,
    private titleService: TitleService,
    private ruleService: RuleService,
    private toast: ToastNotificationsService,
    private route: ActivatedRoute,
    private location: Location,
    private filter: FilterService
  ) {}

  ngOnInit() {
    this.titleService.manuallySet('Canned responses');

    this.currentOffset = +this.route.snapshot.paramMap.get('offset') || 0;
    this.searchCriteria = this.route.snapshot.paramMap.get('query');

    // If we stashed filters for this key, retrieve and set them
    const stashedFilters = this.filter.retrieveStashedValuesForKey('cannedresponses');

    if (stashedFilters) {
      this.activeFilters = stashedFilters;
      this.segmentFilterDefaults = this.activeFilters;
    } else {
      this.activeFilters = [];
      this.segmentFilterDefaults = [];
    }

    this.loadCannedResponses(this.activeFilters, this.currentOffset, this.searchCriteria);
  }

  loadCannedResponses(filters: Filter[], offset?: number, search?: string) {
    this.loading = true;

    this.ruleService.getRulesets('canned', filters, offset, search).subscribe((res) => {
      if (res.error) {
        this.loading = false;
        this.toast.open(`Error fetching Canned responses`, 'Dismiss', NotificationType.Error);
        this.loading = false;
        return;
      }

      this.loading = false;
      this.paginationData = res.meta?.pagination;
      this.cannedResponses = res.data;
    });
  }

  public searchUpdated(searchVal: string) {
    // This is already debounced by the child presentation component
    const url = this.router.createUrlTree([{ query: searchVal }], { relativeTo: this.route }).toString();

    this.location.go(url);

    this.searchCriteria = searchVal;

    this.loadCannedResponses(this.activeFilters, 0, this.searchCriteria);
  }

  public handleFilterChanged(filters: Filter[]) {
    this.activeFilters = filters;

    // Stash the filter values - to persist through page reloads
    this.filter.stashValuesForKey('cannedresponses', this.activeFilters);

    // Retrieve conversations based on the new filters
    this.loadCannedResponses(this.activeFilters, 0, this.searchCriteria);

    // Zero out the offset from the URL - we have fresh data so need to go back to offset 0
    const url = this.router
      .createUrlTree([this.searchCriteria ? { query: this.searchCriteria } : {}], { relativeTo: this.route })
      .toString();

    this.location.go(url);
  }

  public handlePageChanged(newPageNo: number) {
    this.currentOffset = newPageNo;

    // We use offsets as the API deals in offsets rather than pages, when requesting data
    const newOffset = newPageNo * this.paginationData.per_page - this.paginationData.per_page;

    // Commit the new offset to the URL - our source of truth for offsets
    const url = this.router
      .createUrlTree(
        [
          {
            offset: newOffset,
            query: this.searchCriteria ? this.searchCriteria : '',
          },
        ],
        { relativeTo: this.route }
      )
      .toString();

    this.location.go(url);
    this.titleService.manuallySet('Canned responses');

    this.loadCannedResponses(this.activeFilters, newOffset, this.searchCriteria);
  }

  createNewCannedResponse() {
    const payload = {
      name: 'New canned response...',
      event: 'static:canned',
      mode: 'passive',
      all_platforms: true,
      all_hosts: true,
      all_properties: true,
    };

    this.ruleService.createRuleset(payload).subscribe((res) => {
      if (res.data.id) {
        this.router.navigate(['/gx/canned-responses/resp', res.data.id]);
      }
    });
  }

  addNewClicked() {
    this.createNewCannedResponse();
  }

  deleteRuleset(id) {
    this.ruleService.deleteRuleset(id).subscribe((res) => {
      if (res.success) {
        const index = this.cannedResponses.findIndex((canned) => canned.id === id);

        if (index !== -1) {
          this.cannedResponses.splice(index, 1);
        }
        this.toast.open(`Canned response deleted successfully`, 'Dismiss', NotificationType.Error);
      }
    });
  }

  copyRuleset(id) {
    this.ruleService.copyRuleset(id).subscribe((res) => {
      if (res.data.id) {
        this.router.navigate(['/gx/canned-responses/resp/', res.data.id]);
      }
    });
  }

  filterReset() {
    this.activeFilters = [];
    // Stash the filter values - to persist through page reloads
    this.filter.stashValuesForKey('cannedresponses', this.activeFilters);

    this.filterComponent.selfReset();
    this.ngOnInit();
  }
}
